import { DateTime } from 'luxon';

let idCounter = 0;

export const nullFn = (): null => null;

export function uniqueId(prefix: string): string {
  // eslint-disable-line
  idCounter += 1;
  return prefix ? `${prefix}-${idCounter}` : String(idCounter);
}

export function isNumber(value?: string | string[] | null): boolean {
  const NUMBER_REGEX = /^[0-9]{0,8}$/;

  if (!value) return false;
  return !Array.isArray(value) && NUMBER_REGEX.test(value) && value !== '';
}

export function getHourValue(value?: DateTime): string {
  if (value) {
    const hours = ensureCorrectLength(value.hour);
    const hours12 = get12Hours(hours);

    return hours12;
  }

  return '';
}

export function getMinValue(value?: DateTime): string {
  if (value) {
    const minutes = ensureCorrectLength(value.minute);
    return minutes;
  }

  return '';
}

export function getAmPmValue(value?: DateTime): string {
  if (!value) return '';

  const hours = getHoursFromDateTime(value);
  return getAMPM(hours);
}

function getDiff(a: string, b: string): string {
  let i = 0;
  let j = 0;
  let result = '';

  while (j < b.length) {
    if (a[i] !== b[j] || i === a.length) {
      result += b[j];
    } else {
      i += 1;
    }
    j += 1;
  }
  return result;
}

export function getNewTypedValue(
  prevValue?: string,
  value?: string,
): string | null {
  const isValidPrevValue =
    prevValue !== undefined && typeof prevValue === 'string';
  const isValidValue = value !== undefined && typeof value === 'string';

  if (!prevValue && isValidValue) {
    return value;
  }

  if (isValidPrevValue && isValidValue) {
    return getDiff(prevValue, value) || null;
  }

  return null;
}

export function normalizeHour(value?: string | null): string {
  if (isNumber(value)) {
    const number = Number(value);
    if (value === '000') {
      return '12';
    }
    if (number < 10) {
      return `0${number}`;
    }
    if (number > 12 && number < 20) {
      return `0${number - 12}`;
    }
    if (number > 20 && number < 24) {
      return `${number - 12}`;
    }

    return String(number);
  }
  return '';
}

export function getNextHour(value?: string): string {
  const number = Number(value);
  if (!value || number === 12) {
    return '1';
  }

  return String(number + 1);
}

export function getPrevHour(value?: string): string {
  const number = Number(value);
  if (!value || number === 1 || number === 0) {
    return '12';
  }

  return String(number - 1);
}

export function normalizeMinutes(value?: string | null): string {
  if (isNumber(value)) {
    const number = Number(value);
    if (number < 10) {
      return `0${number}`;
    }
    return String(number);
  }
  return '';
}

export function getNextMinute(value?: string): string {
  const number = Number(value);
  if (!value || number === 59) {
    return '0';
  }

  return String(number + 1);
}

export function getPrevMinute(value?: string): string {
  const number = Number(value);
  if (!value || number === 0) {
    return '59';
  }

  return String(number - 1);
}

export function getNextAmPmValue(value?: string) {
  if (value === 'AM' || value === undefined) {
    return 'PM';
  }

  return 'AM';
}

export function isChecked({
  inputValue,
  value,
  defaultValue,
}: {
  inputValue: string;
  value?: string | number;
  defaultValue?: string;
}): boolean {
  if (value) {
    return inputValue === value;
  }
  if (defaultValue) {
    return inputValue === defaultValue;
  }
  return inputValue === 'AM';
}

export function get12HourTime(value?: DateTime): string | undefined {
  if (!value) return undefined;

  const hours = value.hour;
  const mins = value.minute;

  return `${hours}:${mins}`;
}

function ensureCorrectLength(value: number): string {
  if (value < 10) {
    return `0${value}`;
  }

  return `${value}`;
}

function get12Hours(hour: string): string {
  const hourNumber = Number(hour);
  if (hourNumber === 0) {
    return '12';
  }
  if (hourNumber > 12 && hourNumber < 24) {
    if (hourNumber < 22) {
      return `0${String(hourNumber - 12)}`;
    }
    return String(hourNumber - 12);
  }
  return hour;
}

function getAMPM(hour: string | number) {
  const hourNumber = Number(hour);
  if (hourNumber > 11) {
    return 'PM';
  }
  return 'AM';
}

export function get24HourTimeNumber({
  hour,
  minutes,
  ampm,
  zone,
}: {
  hour: string;
  minutes: string;
  ampm: string;
  zone?: string;
}): DateTime {
  const hour24 = get24Hour(hour, ampm);
  const now = DateTime.now();

  const dt = zone
    ? DateTime.fromObject(
        {
          day: now.day,
          month: now.month,
          year: now.year,
          hour: Number(hour24),
          minute: Number(minutes),
          second: 0,
          millisecond: 0,
        },
        { zone: zone },
      )
    : now.set({
        hour: Number(hour24),
        minute: Number(minutes),
        second: 0,
        millisecond: 0,
      });

  return dt;
}

export function get24HourTime({
  hour,
  minutes,
  ampm,
}: {
  hour: string;
  minutes: string;
  ampm: string;
}): string {
  const hour24 = get24Hour(hour, ampm);
  return `${hour24}:${minutes}`;
}

function get24Hour(hour: string, ampm: string) {
  const hoursNumber = Number(hour);
  if (ampm === 'AM') {
    if (hoursNumber === 12) {
      return '00';
    }
    return hour;
  }

  if (ampm === 'PM') {
    if (hoursNumber === 12) {
      return '12';
    }
    return String(hoursNumber + 12);
  }

  return hour;
}

function getHoursFromDateTime(date: DateTime): string {
  const hours = ensureCorrectLength(date.hour);

  return hours;
}

function getMinutesFromDateTime(date: DateTime): string {
  const minutes = ensureCorrectLength(date.minute);

  return minutes;
}

export function getInputValue(value?: DateTime) {
  if (value === undefined) return '';

  const hours = getHoursFromDateTime(value);
  const minutes = getMinutesFromDateTime(value);
  const hour12 = get12Hours(hours);
  const ampm = getAMPM(hours);

  return `${hour12}:${minutes} ${ampm}`;
}

export function padded(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

export function handleOnParsedTimeZone(value?: string | number, zone?: string) {
  if (!value) return undefined;
  if (!zone) return value;

  const setDate = new Date(value);
  const zonedDateTime = DateTime.fromISO(
    `${setDate.getFullYear()}-${padded(setDate.getUTCMonth())}-${padded(
      setDate.getDate(),
    )}T${padded(setDate.getHours())}:${padded(setDate.getMinutes())}:${padded(
      setDate.getSeconds(),
    )}`,
    { zone: zone },
  );

  return zonedDateTime.valueOf();
}
